import { Button } from "@babackapp/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@babackapp/ui/components/dropdown-menu";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@babackapp/ui/components/toggle-group";
import type { Editor } from "@tiptap/react";
import {
  BoldIcon,
  ChevronDownIcon,
  ItalicIcon,
  Link2Icon,
  PaletteIcon,
  RotateCcw,
  UnderlineIcon,
} from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { LinkModal } from "./link-modal";
import { useBlockPanelPropsContext } from "../../lib/panel/panel-container";

interface EmailPanelToolbarProps {
  editor: Editor | null;
}

export function EmailPanelToolbar({ editor }: EmailPanelToolbarProps) {
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const colorInputRef = useRef<HTMLInputElement>(null);

  const currentColor = editor?.getAttributes("textStyle")?.color || "#000000";

  const { variables } = useBlockPanelPropsContext();

  const activeFormats = useMemo(() => {
    if (!editor) return [];

    const formats = [];
    if (editor.isActive("bold")) formats.push("bold");
    if (editor.isActive("italic")) formats.push("italic");
    if (editor.isActive("underline")) formats.push("underline");
    return formats;
  }, [
    editor?.isActive("bold"),
    editor?.isActive("italic"),
    editor?.isActive("underline"),
  ]);

  if (!editor) {
    return null;
  }

  const getCurrentFontSize = () => {
    const fontSize = editor.getAttributes("textStyle")?.fontSize || "16px";
    return fontSize;
  };

  const handleFormatChange = (values: string[]) => {
    if (values.includes("bold") && !editor.isActive("bold")) {
      editor.chain().focus().toggleBold().run();
    } else if (!values.includes("bold") && editor.isActive("bold")) {
      editor.chain().focus().toggleBold().run();
    }

    if (values.includes("italic") && !editor.isActive("italic")) {
      editor.chain().focus().toggleItalic().run();
    } else if (!values.includes("italic") && editor.isActive("italic")) {
      editor.chain().focus().toggleItalic().run();
    }

    if (values.includes("underline") && !editor.isActive("underline")) {
      editor.chain().focus().toggleUnderline().run();
    } else if (!values.includes("underline") && editor.isActive("underline")) {
      editor.chain().focus().toggleUnderline().run();
    }
  };

  const handleFontSizeChange = (fontSize: string) => {
    editor.chain().focus().setFontSize(fontSize).run();
  };

  const getFontSizeLabel = () => {
    const fontSize = getCurrentFontSize();
    return fontSize;
  };

  const getCurrentLink = () => {
    if (!editor) return "";
    const attrs = editor.getAttributes("link");
    return attrs.href || "";
  };

  const handleLinkSave = (href: string) => {
    if (href) {
      editor.chain().focus().setLink({ href }).run();
    } else {
      editor.chain().focus().unsetLink().run();
    }
  };

  const handleLinkClick = () => {
    if (editor.isActive("link")) {
      editor.chain().focus().extendMarkRange("link").run();
    }
    setIsLinkModalOpen(true);
  };

  const handleClearColor = () => {
    if (editor) {
      editor.chain().focus().unsetColor().run();
    }
  };

  return (
    <div
      className="flex gap-1.5 items-center flex-wrap text-foreground"
      ref={containerRef}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-16 bg-background border shadow justify-between text-xs gap-0"
          >
            {getFontSizeLabel()}
            <ChevronDownIcon className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side="bottom"
          align="start"
          className="w-16 text-foreground"
          container={containerRef.current}
        >
          <DropdownMenuItem onClick={() => handleFontSizeChange("12px")}>
            12px
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleFontSizeChange("14px")}>
            14px
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleFontSizeChange("16px")}>
            16px
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleFontSizeChange("18px")}>
            18px
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleFontSizeChange("20px")}>
            20px
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleFontSizeChange("24px")}>
            24px
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ToggleGroup
        variant="default"
        type="multiple"
        value={activeFormats}
        onValueChange={handleFormatChange}
        className="bg-background border shadow h-8"
      >
        <ToggleGroupItem
          value="bold"
          aria-label="Toggle bold"
          className="h-8 w-8 p-0"
        >
          <BoldIcon className="h-3.5 w-3.5" />
        </ToggleGroupItem>
        <ToggleGroupItem
          value="italic"
          aria-label="Toggle italic"
          className="h-8 w-8 p-0"
        >
          <ItalicIcon className="h-3.5 w-3.5" />
        </ToggleGroupItem>
        <ToggleGroupItem
          value="underline"
          aria-label="Toggle underline"
          className="h-8 w-8 p-0"
        >
          <UnderlineIcon className="h-3.5 w-3.5" />
        </ToggleGroupItem>
      </ToggleGroup>
      <Button
        size="sm"
        variant="outline"
        className="bg-background h-8 w-8 p-0"
        onClick={handleLinkClick}
      >
        <Link2Icon className="h-3.5 w-3.5" />
      </Button>
      <div className="flex bg-background border shadow rounded-md h-8">
        <div className="relative">
          <input
            ref={colorInputRef}
            type="color"
            defaultValue={currentColor}
            onInput={(event) => {
              if (editor) {
                editor
                  .chain()
                  .focus()
                  .setColor(event.currentTarget.value)
                  .run();
              }
            }}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            title="Choose text color"
          />
          <Button
            size="sm"
            variant="ghost"
            className="pointer-events-none rounded-r-none border-r h-8 w-8 p-0"
          >
            <PaletteIcon className="h-3.5 w-3.5" />
          </Button>
        </div>
        <Button
          size="sm"
          variant="ghost"
          className="rounded-l-none h-8 w-8 p-0"
          onClick={handleClearColor}
          title="Clear text color"
        >
          <RotateCcw className="h-3.5 w-3.5" />
        </Button>
      </div>
      <LinkModal
        variables={variables}
        open={isLinkModalOpen}
        onOpenChange={setIsLinkModalOpen}
        currentHref={getCurrentLink()}
        onSave={handleLinkSave}
      />
    </div>
  );
}
